<template>
  <div class="recaptcha-wrapper">
    <!-- Dev mode: auto-bypass -->
    <div v-if="isDevMode" class="recaptcha-dev-bypass">
      <i class="zmdi zmdi-check-circle" />
      <span>reCAPTCHA bypassed (dev mode)</span>
    </div>
    <!-- Production: real widget -->
    <template v-else>
      <div :id="containerId" class="recaptcha-container" />
      <p v-if="error" class="recaptcha-error">{{ error }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import {
  loadRecaptcha,
  renderRecaptchaWidget,
  resetRecaptchaWidget,
  getRecaptchaResponse,
  executeRecaptchaWidget,
} from '@/shared/utils/recaptcha'

const DEV_CAPTCHA_TOKEN = 'DEV_CAPTCHA_BYPASS_TOKEN'
const isDevMode = import.meta.env.DEV

const props = defineProps<{
  containerId?: string
}>()

const emit = defineEmits<{
  (e: 'verified', token: string): void
  (e: 'expired'): void
  (e: 'error'): void
}>()

const containerId = props.containerId || `recaptcha-${Math.random().toString(36).slice(2, 9)}`
const widgetId = ref<number | null>(null)
const error = ref('')

const token = ref('')

onMounted(async () => {
  // Dev mode: auto-emit verified with mock token
  if (isDevMode) {
    token.value = DEV_CAPTCHA_TOKEN
    emit('verified', DEV_CAPTCHA_TOKEN)
    console.log('[GonkaGame] reCAPTCHA bypassed in dev mode')
    return
  }

  try {
    error.value = ''
    await loadRecaptcha()
    
    widgetId.value = await renderRecaptchaWidget(containerId, {
      onSuccess: (newToken) => {
        token.value = newToken
        emit('verified', newToken)
      },
      onError: () => {
        error.value = 'Ошибка при проверке reCAPTCHA'
        emit('error')
      },
      onExpired: () => {
        token.value = ''
        emit('expired')
      },
    })
  } catch (err) {
    error.value = 'Не удалось загрузить reCAPTCHA'
    console.error('[GonkaGame] reCAPTCHA load error:', err)
  }
})

onBeforeUnmount(() => {
  // Widget cleanup handled by Google
})

/**
 * Reset the widget for re-verification
 */
const reset = () => {
  if (widgetId.value !== null) {
    resetRecaptchaWidget(widgetId.value)
    token.value = ''
  }
}

/**
 * Get current token (for external use)
 */
const getToken = (): string => {
  if (widgetId.value !== null) {
    return getRecaptchaResponse(widgetId.value)
  }
  return ''
}

/**
 * Trigger manual verification (for size: 'invisible')
 */
const showCaptcha = () => {
  if (widgetId.value !== null) {
    executeRecaptchaWidget(widgetId.value)
  }
}

// Expose methods for parent components
defineExpose({
  reset,
  getToken,
  showCaptcha,
})
</script>

<style scoped>
.recaptcha-wrapper {
  margin: 16px 0;
}

.recaptcha-container {
  display: flex;
  justify-content: center;
}

.recaptcha-dev-bypass {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--ds-success-soft);
  border: 1px dashed var(--ds-success-soft);
  border-radius: var(--ds-radius-control);
  color: var(--hm-success);
  font-size: var(--p-text-13);
  font-weight: var(--ds-weight-medium);
}

.recaptcha-dev-bypass i {
  font-size: var(--p-text-18);
}

.recaptcha-error {
  color: var(--hm-danger);
  font-size: var(--ds-text-size-body);
  margin-top: 8px;
  text-align: center;
}
</style>
